import { CUSTOM_HEADERS, User, UserRoleType } from '@zspace/types';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RouteObject as DefaultReactRouter,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { routes as accountRoutes } from './accounts/routes';
import { routes as customerUserRoutes } from './customers/routes';
import { routes as partnersRoutes } from './partners/routes';
import { routes as salesOrderRoutes } from './sales-orders/routes';
import AppErrorElement from './shared/app-error-element/app-error-element';
import Conditional from './shared/conditional/conditional';
import { ErrorsProvider } from './shared/context/errors-context';
import { HomeSectionsProvider } from './shared/context/home-sections-context';
import { ToastsProvider } from './shared/context/toasts-context';
import { UsersProvider } from './shared/context/users-context';
import useUser from './shared/hooks/user';
import { http } from './shared/http';
import RootLayout from './shared/layout/layout';
import UserErrorElement from './shared/user-error-element/user-error-element';
import PageSpinner from './ui/page-spinner/page-spinner';
import ResponsiveRouteWrapper from './ui/responsive-route-wrapper/responsive-route-wrapper';
import { UserInitializedStorageService } from './users/user-initialized-storage-service';
import { fetchMe } from './users/users-service';
import { routes as zspaceInternalRoutes } from './zspace-internal/routes';

const userInitializedStorageService = new UserInitializedStorageService();

export type RouteObject = DefaultReactRouter & {
  isMobileFriendly?: boolean;
  children?: RouteObject[];
};

export function AppRouter() {
  const { user, loading, error: userRequestError } = useUser();

  const featureRoutes = useMemo(() => {
    let featureRoutes: RouteObject[] = [];
    const userActiveRole = user?.activeRole;

    if (
      !user ||
      loading ||
      userRequestError ||
      user.roles.length === 0 ||
      !userActiveRole
    )
      return featureRoutes;

    switch (userActiveRole?.role.name) {
      case UserRoleType.CUSTOMER:
        featureRoutes = customerUserRoutes;
        break;
      case UserRoleType.ZSPACE_INTERNAL:
        featureRoutes = zspaceInternalRoutes;
        break;
      case UserRoleType.PARTNER:
        featureRoutes = partnersRoutes;
        break;
    }
    return featureRoutes;
  }, [loading, user, userRequestError]);

  const resolveResponsiveRoutes = useCallback((routes: RouteObject[]) => {
    return routes.map((route) => {
      const responsiveRoute: RouteObject = route;

      if (route.element) {
        responsiveRoute.element = (
          <ResponsiveRouteWrapper route={route}>
            {route.element}
          </ResponsiveRouteWrapper>
        );
      }
      if (route.children) {
        responsiveRoute.children = resolveResponsiveRoutes(route.children);
      }

      return responsiveRoute;
    });
  }, []);

  const router = useMemo(() => {
    const childRoutes = [
      ...featureRoutes,
      ...salesOrderRoutes,
      ...accountRoutes,
    ];

    return createBrowserRouter([
      {
        path: '/',
        element: <RootLayout availableRoutes={childRoutes} />,
        errorElement: <AppErrorElement />,
        children: resolveResponsiveRoutes(childRoutes),
      },
    ]);
  }, [featureRoutes, resolveResponsiveRoutes]);

  return (
    <Conditional condition={!!userRequestError}>
      <Conditional.True>
        <UserErrorElement error={userRequestError!} />
      </Conditional.True>
      <Conditional.False>
        <Conditional condition={loading}>
          <Conditional.True>
            <PageSpinner />
          </Conditional.True>
          <Conditional.False>
            <RouterProvider router={router} />
          </Conditional.False>
        </Conditional>
      </Conditional.False>
    </Conditional>
  );
}

function App() {
  const [user, setUser] = useState<User>({} as User);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [userError, setUserError] = useState<AxiosError>();

  const fetchUser = useCallback(async () => {
    try {
      const userInitialized = userInitializedStorageService.get();
      const userResponse = await fetchMe(userInitialized);
      setUser(userResponse);
      userInitializedStorageService.markAsInitialized();
    } catch (error) {
      setUserError(error as AxiosError);
      userInitializedStorageService.markAsNotInitialized();
    } finally {
      setLoading(false);
    }
  }, []);

  const refetchUser = useCallback(async () => {
    try {
      setIsRefetching(true);
      const userInitialized = userInitializedStorageService.get();
      // Ensure that the user is refetched without the cache
      const userResponse = await fetchMe(userInitialized, true);
      setUser(userResponse);
      userInitializedStorageService.markAsInitialized();
    } catch (error) {
      setUserError(error as AxiosError);
      userInitializedStorageService.markAsNotInitialized();
    } finally {
      setIsRefetching(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const activeRoleResponseInterceptor = http.interceptors.response.use(
      (response) => {
        const currentUserActiveRole =
          response.headers[CUSTOM_HEADERS.USER_ACTIVE_ROLE];
        const contextUserActiveRole = user.activeRole?.role.name;
        const activeRoleChanged =
          currentUserActiveRole &&
          contextUserActiveRole &&
          currentUserActiveRole !== contextUserActiveRole;

        /* If the latest backend response returns a user active role different from
        the one currently stored in the context, it means that the user has switched roles
        in another browser tab or session. In this case, we need to reload in order to
        refresh the context and display the correct role. */
        if (activeRoleChanged) {
          window.location.reload();
        }

        return response;
      }
    );

    return () => {
      http.interceptors.response.eject(activeRoleResponseInterceptor);
    };
  }, [user]);

  return (
    <ErrorsProvider>
      <UsersProvider
        user={user}
        loading={loading}
        isRefetching={isRefetching}
        error={userError}
        refetchUser={refetchUser}
      >
        <ToastsProvider>
          <HomeSectionsProvider>
            <AppRouter />
          </HomeSectionsProvider>
        </ToastsProvider>
      </UsersProvider>
    </ErrorsProvider>
  );
}

export default App;
